// THE ANALYZE AND VALUE STEPS' LAYOUT RULES (WEB-PARITY-4) — which of the app's sentences a SERVED fact selects, and how
// a served figure is printed. Pure: nothing here decides a fact or computes a figure the server owns; each rule is the
// app's, cited. The two exceptions are the suggested-value sheet's previews of what the owner is still TYPING (the
// "≈ $X below" line, the conversion note and the two notes under the field), which run before any call could answer —
// the exception path Batch 3 took for its sheet (docs/THIN_CLIENT_DEBT.md, added at birth).

import type { EvaluateCaseResponse, Money, ValueResponse } from "@contract";
import { money } from "../../format";
import { friendlySaleDate, monthYearPhrase, percentString } from "./detailsRules";
import { dollars } from "./parity3Rules";
import { say4, say4Joined, say4With, type Parity4WordingId } from "./parity4Wording";

type EvaluateBody = EvaluateCaseResponse["body"];
type Narrative = NonNullable<EvaluateBody["narrative"]>;
export type Verdict = NonNullable<Narrative["verdict"]>;
type ValueBody = ValueResponse["body"];
type R3Value = NonNullable<Extract<EvaluateCaseResponse["decisions"]["R-3"], { ran: true }>["value"]>;

export { dollars, percentString };

// ── the check's verdict (Models.swift:2925-3035; AnalyzeView.swift:341-361, :854-883) ─────────────────────────────

export type Tint = "savings" | "primary" | "muted" | "accent";

/** `CaseVerdict.tone` → the card's tint (AnalyzeView.swift:878-888): favourable emerald, neutral periwinkle, info amber. */
export function verdictTint(verdict: Verdict | null): Tint {
  switch (verdict) {
    case "strongCase":
    case "possibleCase":
      return "savings";
    case "borderline":
    case "inconclusive":
      return "primary";
    case "insufficientData":
    case "noCaseAcquisitionValue":
    case "noCaseAvmCorroborated":
      return "accent";
    default:
      return "primary";
  }
}

export type VerdictIcon = "seal" | "scale" | "diamond" | "question" | "shield";

/** `CaseVerdict.icon` (Models.swift:2981-2989). */
export function verdictIcon(verdict: Verdict | null): VerdictIcon {
  switch (verdict) {
    case "strongCase":
    case "possibleCase":
      return "seal";
    case "borderline":
      return "scale";
    case "inconclusive":
      return "diamond";
    case "noCaseAcquisitionValue":
    case "noCaseAvmCorroborated":
      return "shield";
    default:
      return "question";
  }
}

/** `VerdictCard.displayTitle` (AnalyzeView.swift:871-876): the weak-case softening, else the model's headline, else the
 *  verdict's own title. */
export function verdictTitle(narrative: Narrative, precision: "high" | "medium" | "low" | null, submarket: boolean): string {
  if (narrative.verdict === "noCaseAvmCorroborated" && (precision === "low" || submarket)) return say4("verdict.weakCase");
  if (narrative.headline.trim() !== "") return narrative.headline;
  return narrative.verdict === null ? narrative.headline : say4(`verdict.title.${narrative.verdict}` as Parity4WordingId);
}

/** `CaseVerdict.recommendation` (Models.swift:3013-3037): none on the favourable verdicts. */
export function recommendation(verdict: Verdict | null): string | null {
  switch (verdict) {
    case "borderline":
    case "inconclusive":
    case "insufficientData":
    case "noCaseAcquisitionValue":
    case "noCaseAvmCorroborated":
      return say4Joined(`verdict.rec.${verdict}`);
    default:
      return null;
  }
}

/** `analyzeCardFooter` (AnalyzeView.swift:344-362), by the verdict on file — the id of the app's line. */
export function analyzeFooter(verdict: Verdict | null): Parity4WordingId {
  switch (verdict) {
    case "noCaseAcquisitionValue":
      return "analyze.footer.acquisition";
    case "noCaseAvmCorroborated":
      return "analyze.footer.avm";
    case "borderline":
    case "inconclusive":
      return "analyze.footer.recommendation";
    default:
      return "analyze.footer.default";
  }
}

/** `CaseStrength.meterHeadline` (CaseStrength.swift:42-44): the four comp tiers take " reduction"; the others stand alone. */
export function meterHeadline(label: string): string {
  return ["Very strong", "Strong", "Solid", "Fair"].includes(label) ? say4With("meter.headline", { label }) : label;
}

/** `Confidence.precisionLabel` (SuggestedValue.swift:83-89). */
export function precisionLabel(precision: "high" | "medium" | "low"): string {
  return say4(`precision.${precision}`);
}

/** "Your N strongest comps: X–Y sq ft homes that sold $A–$B." (AnalyzeView.swift:727-747, ValueView.swift:91-107), over
 *  the server's figures. */
export function strongestLine(f: NonNullable<ValueBody["value"]["strongestComps"]>): string {
  const plural = f.count === 1 ? "" : say4("verdict.strongest.plural");
  let line = say4With("verdict.strongest.head", { count: f.count, plural });
  if (f.sqftLow !== null && f.sqftHigh !== null) {
    const size = f.sqftLow === f.sqftHigh ? `${f.sqftLow}` : say4With("verdict.strongest.sizeRange", { low: f.sqftLow, high: f.sqftHigh });
    line += say4With("verdict.strongest.size", { size, plural });
  }
  if (f.priceLow !== null && f.priceHigh !== null) {
    line += say4("verdict.strongest.sold") + (f.priceLow.amount === f.priceHigh.amount
      ? money(f.priceLow)
      : say4With("verdict.strongest.priceRange", { low: money(f.priceLow), high: money(f.priceHigh) }));
  }
  return line + say4("verdict.strongest.end");
}

// ── the number line (ValueNumberLine.swift) ────────────────────────────────────────────────────────────────────────

/** Where each figure sits on the track, as fractions of its width (ValueNumberLine.swift:36-46): a tenth of the span of
 *  padding either side. Geometry only — every figure is the server's. */
export function lineGeometry(assessed: number, low: number, high: number, target: number) {
  const span = Math.max(1, assessed - Math.min(low, target));
  const min = Math.min(low, target) - span * 0.1;
  const max = assessed + span * 0.1;
  const frac = (v: number) => (max - min > 0 ? (v - min) / (max - min) : 0);
  return { low: frac(low), high: frac(high), target: frac(target), assessed: frac(assessed) };
}

// ── the no-case page (NoCaseVerdict.swift) ─────────────────────────────────────────────────────────────────────────

/** `purchaseWhenPhrase` (NoCaseVerdict.swift:215-218): "recently" for none, the raw string where it cannot be read. */
export function purchaseWhenPhrase(raw: string | null): string {
  if (raw === null || raw.trim() === "") return say4("nocase.metric.whenUnknown");
  return monthYearPhrase(raw) ?? raw;
}

export function noCaseResultLine(v: R3Value): string {
  return v.shape === "belowMarket"
    ? say4With("nocase.result.belowMarket", { gap: money(v.gapAbove) })
    : say4("nocase.result.recentPurchase");
}

/** `NoCaseVerdict.verdict` (:89-99): the core sentence, and the state's can-raise sentence where it is known. */
export function noCaseVerdictLine(v: R3Value): string {
  const core = v.shape === "belowMarket"
    ? say4With("nocase.verdict.belowMarket", { basis: money(v.assessedBasis) })
    : say4With("nocase.verdict.recentPurchase", { basis: money(v.assessedBasis) });
  return v.canRaiseSentence === null ? core : `${core} ${v.canRaiseSentence.text}`;
}

export function noCaseEvidence(v: R3Value): string {
  return v.shape === "belowMarket"
    ? say4Joined("nocase.evidence.belowMarket", { count: v.compCount })
    : say4Joined("nocase.evidence.recentPurchase");
}

/** `NoCaseVerdict.metrics` (:177-196). */
export function noCaseMetrics(v: R3Value): { value: string; label: string }[] {
  if (v.shape === "belowMarket") {
    return [
      { value: `${v.compCount}`, label: say4("nocase.metric.sourced") },
      { value: money(v.adjustedMidpoint), label: say4("nocase.metric.midpoint") },
      { value: say4With("nocase.metric.aboveValue", { percent: v.percentAbove }), label: say4("nocase.metric.above") },
    ];
  }
  return [
    { value: `${v.compCount}`, label: say4("nocase.metric.sourced") },
    { value: v.ownPurchase === null ? say4("nocase.metric.paidNone") : money(v.ownPurchase), label: say4("nocase.metric.paid") },
    { value: purchaseWhenPhrase(v.ownPurchaseWhen), label: say4("nocase.metric.when") },
  ];
}

// ── the neighbourhood card (AnalyzeView.swift:505-535) ─────────────────────────────────────────────────────────────

type Hood = NonNullable<Extract<ValueResponse["decisions"]["NEW-14"], { ran: true }>["value"]>;

export function hoodLines(v: Hood): { counts: string; basis: string; majority: string; caution: boolean } {
  return {
    counts: say4With("hood.counts", { total: v.total, below: v.below, above: v.above }),
    basis: say4With("hood.basis", { rate: dollars(v.subjectPerSqft) }),
    majority: say4(`hood.${v.majority}`),
    caution: v.majority !== "mostlyBelow",
  };
}

// ── the Value step (ValueView.swift) ──────────────────────────────────────────────────────────────────────────────

/** The hero's sentence (:179-223), by the server's case. */
export function heroSubtitle(hero: ValueBody["value"]["hero"]): string {
  switch (hero.kind) {
    case "acquisitionNoCase":
      return say4("value.hero.acquisitionNoCase");
    case "noSuggestionCounted":
      return say4With("value.hero.counted", {
        count: hero.supporting === 0 ? say4("value.hero.counted.none") : say4With("value.hero.counted.only", { supporting: hero.supporting }),
        usable: hero.usable,
      });
    case "noSuggestion":
      return say4("value.hero.noSuggestion");
    default:
      return say4("value.hero.suggestion");
  }
}

/** `ratioTermShort` (Models.swift:1197-1204), "CLR" written out (D9). */
export function ratioTerm(state: string | null | undefined): string {
  switch ((state ?? "").trim().toUpperCase()) {
    case "PA":
      return say4("term.pa");
    case "NJ":
      return say4("term.nj");
    case "NH":
      return say4("term.nh");
    default:
      return say4("term.other");
  }
}

/** `assessedEquivalentNote(ofMarket:)` (Models.swift:1212-1215), over a SERVED assessed equivalent: nil at full value. */
export function assessedNote(assessed: Money<"assessed"> | null, ratio: ValueBody["value"]["ratio"], state: string | null): string | null {
  if (!ratio.fractional || assessed === null || !(assessed.amount > 0)) return null;
  return say4With("note.assessedEquivalent", { assessed: money(assessed), pct: percentString(ratio.value), term: ratioTerm(state) });
}

/** The purchase warning (ValueView.swift:379-383). */
export function purchaseWarning(purchase: Money<"market">, when: string | null, percentBelow: number): string {
  const phrase = when === null ? null : friendlySaleDate(when);
  return say4With("value.purchase.1", { purchase: money(purchase) })
    + (phrase === null ? "" : say4With("value.purchase.in", { when: phrase }))
    + say4With("value.purchase.2", { percent: Math.trunc(percentBelow) });
}

/** `String.titleCasedAddress` — each word's first letter upper-cased, the rest lowered, as the app prints an address. */
export function titleCased(address: string): string {
  return address.toLowerCase().replace(/(^|[\s-])(\S)/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
}

/** "Why we stopped here" (ValueView.swift:464-479). */
export function stoppedHere(c: NonNullable<ValueBody["value"]["contradicting"]>): { body: string; lead: string } {
  const others = c.others > 0
    ? say4With("value.stopped.others", { count: c.others, verb: say4(c.others === 1 ? "value.stopped.others.one" : "value.stopped.others.many") })
    : "";
  return {
    body: say4With("value.stopped.body", { address: titleCased(c.address), value: money(c.adjustedValue), others }),
    lead: say4(c.compsLead ? "value.stopped.lead" : "value.stopped.noLead"),
  };
}

/** "Capped at what you paid" (ValueView.swift:490-496). */
export function cappedBody(price: Money<"market">, when: string | null): string {
  const phrase = when === null ? null : monthYearPhrase(when);
  return say4With("value.capped.body", {
    price: money(price), when: phrase === null ? "" : say4With("value.capped.in", { when: phrase }),
  });
}

/** `methodLabel` (ValueView.swift:948-954). */
export function methodLabel(method: string): string {
  if (method === "cost-to-cure") return say4("condition.method.costToCure");
  if (method === "age-life") return say4("condition.method.ageLife");
  return say4("condition.method.market");
}

/** `item.category.capitalized` (ValueView.swift:864) — the one enum the app prints, each word capitalised. */
export function capitalized(s: string): string {
  return s.toLowerCase().replace(/(^|\s)(\S)/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
}

/** `pct(_:)` (ValueView.swift:1503): one decimal and a percent sign. */
export function ratioPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

/** `independentEstimatePhrase` (Models.swift:1424-1431), over the served estimate. */
export function estimatePhrase(e: { estimate: Money<"market">; low: Money<"market"> | null; high: Money<"market"> | null }): string {
  const caveat = say4("independent.caveat");
  if (e.low !== null && e.high !== null && e.high.amount > e.low.amount) {
    return say4With("independent.withRange", { estimate: money(e.estimate), low: money(e.low), high: money(e.high), caveat });
  }
  return say4With("independent.plain", { estimate: money(e.estimate), caveat });
}

/** The uniformity footer's state name (ValueView.swift:1494-1500). */
export function uniformityState(state: string | null | undefined): string {
  const code = (state ?? "").trim().toUpperCase().slice(0, 2);
  return code === "TX" ? say4("uniformity.state.tx") : code === "NJ" ? say4("uniformity.state.nj") : say4("uniformity.state.other");
}

// ── the suggested-value sheet (SuggestedValue.swift:2227-2577) ─────────────────────────────────────────────────────

type Suggestion = NonNullable<ValueBody["value"]["suggestion"]>;

const WORD: Record<"high" | "medium" | "low", Parity4WordingId> = {
  high: "sheet.word.high", medium: "sheet.word.medium", low: "sheet.word.low",
};

export function confidenceWord(c: "high" | "medium" | "low"): string {
  return say4(WORD[c]);
}

/** "≈ $X (N%) below your $B implied market value" (:2250-2262), for the figure in the headline — what the owner may be
 *  typing, so this runs on the page by the exception path (see this file's header). */
export function sheetBelowLine(basis: number | null, shown: number, fractional: boolean): string | null {
  if (basis === null || !(basis > shown)) return null;
  const red = basis - shown;
  const pct = Math.round(red / basis * 100);
  return say4With("sheet.below", {
    amount: dollars(red),
    percent: pct > 0 ? say4With("sheet.below.percent", { percent: pct }) : say4("sheet.below.underOne"),
    basis: dollars(basis),
    label: say4(fractional ? "sheet.basis.market" : "sheet.basis.assessment"),
  });
}

/** The conversion note under a typed figure (`assessedEquivalentNote`, Models.swift:1212-1215) — a preview of unsent
 *  typing at the property's served ratio, the exception path. */
export function typedAssessedNote(typed: number | null, ratio: ValueBody["value"]["ratio"], state: string | null): string | null {
  if (typed === null || !ratio.fractional || !(typed > 0)) return null;
  return say4With("note.assessedEquivalent", {
    assessed: dollars(Math.round(typed * ratio.value)), pct: percentString(ratio.value), term: ratioTerm(state),
  });
}

/** The two notes under "Or set your own amount" (:2283-2308): below the ASSESSED figure in a fractional county, and how a
 *  figure more than $500 off the suggestion changes the cited sales. Unsent typing; the exception path. */
export function typedNotes(typed: number | null, s: Suggestion, assessed: number | null, fractional: boolean): string[] {
  const out: string[] = [];
  if (typed === null || !(typed > 0)) return out;
  if (fractional && assessed !== null && assessed > 0 && typed < assessed) {
    out.push(say4With("sheet.own.belowAssessed", { assessed: dollars(assessed) }));
  }
  if (Math.abs(typed - s.value.amount) > 500) {
    out.push(say4With(typed < s.value.amount ? "sheet.own.askBelow" : "sheet.own.askAbove", { value: money(s.value) }));
  }
  return out;
}

/** The size bullet (:2505-2519). */
export function sizeBullet(s: Suggestion, fractional: boolean, pct: string, term: string): string {
  const prefix = s.subjectSqft !== null && s.subjectSqft > 0
    ? say4With("sheet.size.withSqft", { sqft: Math.round(s.subjectSqft).toLocaleString("en-US") })
    : say4("sheet.size.noSqft");
  return fractional
    ? say4With("sheet.size.fractional", { prefix, market: money(s.marketValue), assessed: money(s.marketValueAssessed), pct, term })
    : say4With("sheet.size.plain", { prefix, indicated: money(s.indicatedValue) });
}

// ── the "Quick check" (LargeGapVerificationView.swift) ────────────────────────────────────────────────────────────

type LargeGap = NonNullable<ValueBody["value"]["largeGap"]>;

export function lgExplanation(g: LargeGap): string {
  const gap = say4With("lg.gap", { percent: g.reductionPercent });
  return say4With(g.sizeMismatch ? "lg.gap.mismatch" : "lg.gap.record", { gap });
}

/** "Your comps are X–Y sq ft. Your home is Z sq ft." (:164-170), and whether it sits inside the range (:174-178). */
export function lgSizeLine(g: LargeGap): { line: string; within: boolean } | null {
  if (g.compSqftLow === null || g.compSqftHigh === null) return null;
  const low = Math.trunc(g.compSqftLow);
  const high = Math.trunc(g.compSqftHigh);
  const range = low === high ? say4With("lg.size.one", { sqft: low }) : say4With("lg.size.range", { low, high });
  const home = g.subjectSqft === null ? "" : say4With("lg.size.home", { sqft: Math.trunc(g.subjectSqft) });
  return {
    line: say4With("lg.size.line", { range, home }),
    within: g.subjectSqft !== null && g.subjectSqft >= g.compSqftLow && g.subjectSqft <= g.compSqftHigh,
  };
}

/** What the owner confirms, as the app files it (`reasoning`, :265-271). */
export function lgReasoning(g: LargeGap): string[] {
  return [say4(g.sizeMismatch ? "lg.reason.mismatch" : "lg.reason.match")];
}

// ── the grounds (ValueView.swift:1651-1807) ───────────────────────────────────────────────────────────────────────

export const SELECTABLE_REASONS = ["overvaluation", "comparableSales", "conditionIssues", "factualError"] as const;
export type SelectableReason = (typeof SELECTABLE_REASONS)[number];

/** The grounds after a tap (`toggle`, :1808-1814): removed if on file, else appended. */
export function toggled(reasons: readonly string[], reason: string): string[] {
  return reasons.includes(reason) ? reasons.filter((r) => r !== reason) : [...reasons, reason];
}
